import React, { useEffect, useState, useCallback } from 'react';
import { Button, Table, Tag, Input, Space, Popconfirm, message, Select, Tooltip, Switch } from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    EditOutlined,
    DeleteOutlined,
    BellOutlined,
} from '@ant-design/icons';
import { userApi } from '../../services/api';
import { UserItem } from '../../services/types';
import UserFormModal from '../../components/admin/UserFormModal';
import { useAuthStore } from '../../stores/authStore';
import { formatDateTime } from '@/utils/date';

const roleMap: Record<string, { color: string; label: string }> = {
    admin: { color: 'red', label: '管理员' },
    operator: { color: 'blue', label: '运营' },
    user: { color: 'default', label: '用户' },
};

const UsersPage: React.FC = () => {
    const { user } = useAuthStore();
    const [users, setUsers] = useState<UserItem[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [roleFilter, setRoleFilter] = useState<string | undefined>();

    const [modalOpen, setModalOpen] = useState(false);
    const [editData, setEditData] = useState<UserItem | undefined>();

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await userApi.list({
                skip: (page - 1) * pageSize,
                limit: pageSize,
                keyword: keyword || undefined,
                role: roleFilter,
            });
            if (res.success && res.data) {
                setUsers(res.data.items || []);
                setTotal(res.data.total || 0);
            }
        } catch (error) {
            message.error('获取用户列表失败');
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, keyword, roleFilter]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleDelete = async (id: string) => {
        try {
            await userApi.delete(id);
            message.success('删除成功');
            fetchUsers();
        } catch (error: any) {
            message.error(error.response?.data?.detail || '删除失败');
        }
    };

    const handleToggleActive = async (record: UserItem) => {
        try {
            const res = await userApi.toggleActive(record.id);
            if (res.success) {
                message.success(res.message);
                fetchUsers();
            }
        } catch (error: any) {
            message.error(error.response?.data?.detail || '操作失败');
        }
    };

    const handleEdit = (record: UserItem) => {
        setEditData(record);
        setModalOpen(true);
    };

    const columns = [
        {
            title: '用户名',
            dataIndex: 'username',
            key: 'username',
            width: 150,
            render: (val: string, record: UserItem) => (
                <Space>
                    <div
                        style={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            background: record.role === 'admin' ? '#ef4444' : record.role === 'operator' ? '#3b82f6' : '#a855f7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: 12,
                        }}
                    >
                        {val?.[0]?.toUpperCase() || '?'}
                    </div>
                    <span style={{ fontWeight: 500 }}>{val}</span>
                </Space>
            ),
        },
        {
            title: '邮箱',
            dataIndex: 'email',
            key: 'email',
            ellipsis: true,
            width: 200,
        },
        {
            title: '角色',
            dataIndex: 'role',
            key: 'role',
            width: 100,
            render: (val: string) => {
                const info = roleMap[val] || { color: 'default', label: val };
                return <Tag color={info.color}>{info.label}</Tag>;
            },
        },
        {
            title: '状态',
            dataIndex: 'is_active',
            key: 'is_active',
            width: 90,
            render: (val: boolean, record: UserItem) => (
                <Switch
                    checked={val}
                    size="small"
                    onChange={() => handleToggleActive(record)}
                    disabled={record.id === user?.id}
                />
            ),
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 160,
            render: (val: string) =>
                formatDateTime(val),
        },
        {
            title: '操作',
            key: 'actions',
            width: 100,
            render: (_: unknown, record: UserItem) => (
                <Space size={4}>
                    <Tooltip title="编辑">
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="确定删除此用户？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="删除"
                        cancelText="取消"
                        disabled={record.id === user?.id}
                    >
                        <Tooltip title="删除">
                            <Button
                                type="link"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                disabled={record.id === user?.id}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* 顶部栏 */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0 32px',
                    height: 64,
                    background: '#fff',
                    borderBottom: '1px solid #e4e4e7',
                    flexShrink: 0,
                }}
            >
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#18181b' }}>
                    用户权限管理
                </h2>
                <Space>
                    <BellOutlined style={{ fontSize: 18, color: '#71717a', cursor: 'pointer' }} />
                    <div
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: '#3b82f6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: 14,
                        }}
                    >
                        {user?.username?.[0]?.toUpperCase() || 'A'}
                    </div>
                </Space>
            </div>

            {/* 内容区 */}
            <div style={{ flex: 1, padding: 24, overflow: 'auto' }}>
                {/* 操作栏 */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 16,
                    }}
                >
                    <Space>
                        <Input
                            placeholder="搜索用户..."
                            prefix={<SearchOutlined style={{ color: '#a1a1aa' }} />}
                            value={keyword}
                            onChange={(e) => {
                                setKeyword(e.target.value);
                                if (!e.target.value) { setPage(1); }
                            }}
                            onPressEnter={() => { setPage(1); fetchUsers(); }}
                            style={{ width: 280 }}
                            allowClear
                        />
                        <Select
                            placeholder="角色筛选"
                            value={roleFilter}
                            onChange={(val) => { setRoleFilter(val); setPage(1); }}
                            allowClear
                            style={{ width: 120 }}
                            options={[
                                { label: '管理员', value: 'admin' },
                                { label: '运营', value: 'operator' },
                                { label: '用户', value: 'user' },
                            ]}
                        />
                    </Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditData(undefined);
                            setModalOpen(true);
                        }}
                    >
                        添加用户
                    </Button>
                </div>

                {/* 用户表格 */}
                <Table
                    columns={columns}
                    dataSource={users}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: page,
                        pageSize,
                        total,
                        showTotal: (t: number) => `共 ${t} 条`,
                        onChange: (p: number) => setPage(p),
                    }}
                    style={{ background: '#fff', borderRadius: 12 }}
                />
            </div>

            <UserFormModal
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onSuccess={() => {
                    setModalOpen(false);
                    fetchUsers();
                }}
                editData={editData}
                currentUserId={user?.id}
            />
        </div>
    );
};

export default UsersPage;
